import { TableContentHeader } from './interface/tableContentHeaders';
import { NarrowState } from './interface/uiState';
import { objectEquals } from '@/utils/objectEquals';
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from './constants';
import { convertIDateToYYYYMM } from './utils/dateFormat';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * テーブル表示に汎用的に使うロジックをまとめる（表示内容の制御等）
 */
export class BaseTableContentViewModel {
  /**
   * 太字表示の判定
   * narrowStatesにnarrowKeysが含まれている場合太字にする
   * @param narrowStates - 絞り込み状態
   * @param narrowKeys - 絞り込みキー
   */
  public isBold = (narrowStates: NarrowState[], narrowKeys?: string[]): boolean => {
    if (narrowStates.length === 0 || !narrowKeys || narrowKeys.length === 0) {
      return false;
    }
    for (let i = 0; i < narrowStates.length; i++) {
      if (objectEquals(narrowStates?.[i]?.narrowKey, narrowKeys)) {
        return true;
      }
    }
    return false;
  };

  /**
   * 列固定の判定
   * stableColumnCount以下のindexの場合、列固定する
   * @param stableColumnCount - 固定列数
   * @param index - インデックス
   */
  public isSticky = (stableColumnCount: number, index: number): boolean => {
    return index < stableColumnCount;
  };

  /**
   * 列固定を実現するために必要な差分を計算
   * @param stableColumnCount - 固定列数
   * @param index - インデックス
   * @param accumulationTableContentWidth - 累積テーブルコンテンツ幅
   */
  public calcStickyDiff = (stableColumnCount: number, index: number, accumulationTableContentWidth: number): string => {
    return this.isSticky(stableColumnCount, index) ? `${accumulationTableContentWidth}px` : 'auto';
  };

  /**
   * テーブルの列幅が有効な値かどうか
   * @param width - 列幅
   */
  public isTableColumnWidthValid = (width: number): boolean => {
    return width.isValidNumber({ lower: MIN_COLUMN_WIDTH, upper: MAX_COLUMN_WIDTH });
  };

  /**
   * テーブルの列幅の正規化
   * @param width - 列幅
   */
  public clampedTableColumnWidth = (width: number): number => {
    return width.clamp(MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH);
  };

  /**
   * TableContentHeaderから現在のヘッダリストを取得
   * @param tableContentProperties - テーブルコンテンツプロパティ
   */
  public getCurrentHeaderList = (tableContentProperties: TableContentHeader): string[] => {
    return tableContentProperties?.contentProperties?.map((contentProperty) => contentProperty.key);
  };
}

/**
 * テーブルの内示表示に関する汎用的なロジックをまとめる
 */
export class ForecastTableContentViewModel extends BaseTableContentViewModel {
  /**
   * 内示項目のヘッダーを取得する
   * @param index - 内示の中でのindex
   * @param startYearMonth - 内示の開始年月
   */
  _forecastHeader = (index: number, startYearMonth?: IYearMonth): string => {
    // startYearMonthから開始し、yyyy/MM形式でindex番目の月を表示する。年をまたぐ場合も考慮する
    return convertIDateToYYYYMM(
      this.getIYearMonth(startYearMonth ? new Date(startYearMonth.year, startYearMonth.month - 1) : new Date(), index)
    );
  };

  /**
   * 内示の中でのindexを取得する
   * @param keyName - 内示のkeyName
   */
  _getForecastIndex = (keyName: string): number => {
    // "customerAnnounce-${index + 1}"の形式でkeyNameが渡されるので、indexを取得する
    return parseInt(keyName.split('-')[1]) - 1;
  };

  /**
   * baseDateを起点として、index番目の月のIDateを取得する
   * @param baseDate - 起点となる日付
   * @param index - baseDateからの月の差分
   */
  public getIYearMonth = (baseDate: Date, index: number): IYearMonth => {
    // baseDateから開始し、yyyy/MM形式でindex番目の月を表示する。年をまたぐ場合も考慮する
    return DateService.convertDateToIYearMonth(DateService.getShiftedDateByMonths(baseDate, index));
  };
}
